import React from 'react';
import { Heart, Activity, BookOpen, Trees, Users, Palette, Sparkles, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const copingMechanisms = [
  {
    id: 1,
    title: 'Meditation & Mindfulness',
    description: 'Practice being present with your emotions. Meditation can help you process grief without judgment and find moments of inner peace.',
    icon: Heart,
    image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxtZWRpdGF0aW9ufGVufDB8fHx8MTczMzkxNzY0OHww&ixlib=rb-4.1.0&q=85',
    tips: [
      'Start with just 5 minutes daily',
      'Use guided meditation apps',
      'Focus on breathing when overwhelmed',
      'Be gentle with yourself'
    ]
  },
  {
    id: 2,
    title: 'Movement & Exercise',
    description: 'Physical activity releases endorphins and helps process emotional pain. Even a short walk can make a difference.',
    icon: Activity,
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxleGVyY2lzZXxlbnwwfHx8fDE3MzM5MTc2NTJ8MA&ixlib=rb-4.1.0&q=85',
    tips: [
      'Take daily walks in nature',
      'Try gentle yoga or stretching',
      'Dance to your favorite music',
      'Join a fitness class for community'
    ]
  },
  {
    id: 3,
    title: 'Journaling & Writing',
    description: 'Express your feelings on paper. Writing helps organize thoughts, track progress, and release pent-up emotions.',
    icon: BookOpen,
    image: 'https://images.unsplash.com/photo-1586380951230-e6703d9f6833?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxqb3VybmFsaW5nfGVufDB8fHx8MTczMzkxNzY1Nnww&ixlib=rb-4.1.0&q=85',
    tips: [
      'Write letters to your loved one',
      'Keep a daily gratitude journal',
      'Document your journey',
      'No rules - just write freely'
    ]
  },
  {
    id: 4,
    title: 'Connect with Nature',
    description: 'Spending time outdoors can be deeply healing. Nature provides perspective, peace, and a sense of connection.',
    icon: Trees,
    image: 'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxuYXR1cmV8ZW58MHx8fHwxNzMzOTE3NjYwfDA&ixlib=rb-4.1.0&q=85',
    tips: [
      'Visit parks or beaches regularly',
      'Garden or care for plants',
      'Watch sunrises or sunsets',
      'Listen to nature sounds'
    ]
  },
  {
    id: 5,
    title: 'Talk to Others',
    description: 'Sharing your grief with trusted friends, family, or support groups helps you feel less alone and process emotions.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1579208570378-8c970854bc23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxzdXBwb3J0fGVufDB8fHx8MTczMzkxNzY2NHww&ixlib=rb-4.1.0&q=85',
    tips: [
      'Join a grief support group',
      'Talk to understanding friends',
      'Consider professional counseling',
      'Share memories with family'
    ]
  },
  {
    id: 6,
    title: 'Creative Expression',
    description: 'Art, music, crafts, or other creative outlets can help express emotions that words cannot capture.',
    icon: Palette,
    image: 'https://images.pexels.com/photos/159579/crayons-coloring-book-coloring-book-159579.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    tips: [
      'Paint or draw your feelings',
      'Play or listen to music',
      'Create memory scrapbooks',
      'Try adult coloring books'
    ]
  },
  {
    id: 7,
    title: 'Self-Care Rituals',
    description: 'Taking care of your physical needs is essential. Rest, nutrition, and gentle routines support emotional healing.',
    icon: Sparkles,
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxzZWxmLWNhcmV8ZW58MHx8fHwxNzMzOTE3NjY4fDA&ixlib=rb-4.1.0&q=85',
    tips: [
      'Maintain regular sleep schedule',
      'Eat nourishing meals',
      'Take relaxing baths',
      'Practice good hygiene'
    ]
  },
  {
    id: 8,
    title: 'Professional Support',
    description: 'Therapists and counselors trained in grief can provide invaluable guidance and coping strategies.',
    icon: MessageCircle,
    image: 'https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHx0aGVyYXB5fGVufDB8fHx8MTczMzkxNzY3Mnww&ixlib=rb-4.1.0&q=85',
    tips: [
      'Seek grief counseling',
      'Try different therapy types',
      'Join therapist-led groups',
      'Use online therapy platforms'
    ],
    contact: {
      phone: '(321) 262-0256',
      email: 'mentorsplace@gmail.com'
    }
  }
];

const CopingMechanisms = () => {
  return (
    <section id="coping" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Healthy Coping Mechanisms
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            These evidence-based strategies can help you navigate grief in healthy ways. 
            Try different approaches and find what resonates with you.
          </p>
        </div>

        {/* Coping Mechanisms Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
          {copingMechanisms.map((mechanism) => {
            const Icon = mechanism.icon;
            return (
              <Card key={mechanism.id} className="overflow-hidden hover:shadow-2xl transition-shadow duration-300 border-2">
                {/* Image Header */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={mechanism.image}
                    alt={mechanism.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 flex items-center gap-3">
                    <div className="p-2 bg-white/90 rounded-lg backdrop-blur-sm">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{mechanism.title}</h3>
                  </div>
                </div>

                {/* Card Content */}
                <CardHeader className="pb-3">
                  <CardDescription className="text-base text-gray-700 leading-relaxed">
                    {mechanism.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-blue-600">✓</span>
                      How to Practice:
                    </h4>
                    <ul className="space-y-2">
                      {mechanism.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-blue-600 mt-0.5">•</span>
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                    
                    {/* Contact Info for Professional Support */}
                    {mechanism.contact && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Contact for Counseling:</p>
                        <div className="space-y-2">
                          <a 
                            href={`tel:${mechanism.contact.phone.replace(/[^0-9]/g, '')}`}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            📞 {mechanism.contact.phone}
                          </a>
                          <a 
                            href={`mailto:${mechanism.contact.email}`}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            ✉️ {mechanism.contact.email}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Important Note */}
        <div className="mt-16 bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 border-2 border-teal-200">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Remember: Everyone Grieves Differently
            </h3>
            <p className="text-gray-700 leading-relaxed">
              What works for one person may not work for another. Be patient with yourself and 
              try different strategies. It's okay to have good days and bad days. Healing is not 
              linear, and there's no "right" way to cope with grief. If you're struggling, please 
              reach out to a professional for support.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CopingMechanisms;
